/**
 * RewardsTab - Public rewards tab showing either:
 * - New distribution system (Phase 2) if project has rewards_config
 * - Legacy claim system (Phase 1) otherwise
 *
 * Feature: 017-rewards-claim-system + VOC Rewards Phase 2
 */

import React from 'react';
import { Badge } from '../../ui/components/Badge';
import { FeatherGift } from '@subframe/core';
import { ClaimBanner } from './ClaimBanner';
import { usePublicPeriods } from '../../hooks/useRewards';
import { useRewardsConfig, useMyDistributionRewards, usePublicActivePeriodSummary, usePublicDistributionHistory } from '../../hooks/useDistribution';
import { ActivePeriodCard } from './ActivePeriodCard';
import { HolderRewardsSummary } from './HolderRewardsSummary';
import { DistributionHistory } from './DistributionHistory';
import type { ClaimPeriod } from '../../api/rewards';
import { useAuthStore } from '../../store/authStore';
import { featureFlags } from '../../config/featureFlags';

interface RewardsTabProps {
  projectSlug: string;
}

export const RewardsTab: React.FC<RewardsTabProps> = ({ projectSlug }) => {
  const { data: configData, isLoading: configLoading } = useRewardsConfig(projectSlug);

  // If config exists with distribution_mode AND feature flag is enabled, use new system
  const hasDistributionConfig =
    featureFlags.distribution &&
    configData?.config?.distribution_mode != null;

  if (configLoading) {
    return <div className="text-subtext-color py-4">Loading rewards...</div>;
  }

  if (hasDistributionConfig) {
    return <DistributionRewardsTab projectSlug={projectSlug} config={configData!.config} />;
  }

  return <LegacyRewardsTab projectSlug={projectSlug} />;
};

// ---- New Distribution System ----

const DistributionRewardsTab: React.FC<{
  projectSlug: string;
  config: { distribution_mode: string; reward_currency_label: string };
}> = ({ projectSlug, config }) => {
  const { isAuthenticated } = useAuthStore();
  const { data: myData } = useMyDistributionRewards(isAuthenticated ? projectSlug : undefined);
  const { data: activePeriodData, isLoading: activeLoading } = usePublicActivePeriodSummary(projectSlug);
  const { data: historyData, isLoading: historyLoading } = usePublicDistributionHistory(projectSlug);

  const isClaimRequired = config.distribution_mode === 'claim_required';
  const hasClaimed = (myData?.myRewards?.length ?? 0) > 0;

  const hasActivePeriod = !!activePeriodData?.period;
  const hasHistory = (historyData?.periods?.length ?? 0) > 0;
  const isLoading = activeLoading || historyLoading;

  // Show empty state when there's nothing to display
  if (!isLoading && !hasActivePeriod && !hasHistory) {
    return (
      <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
        <div className="flex flex-col items-center gap-2">
          <FeatherGift className="w-8 h-8 text-neutral-400" />
          <span className="text-body font-body text-subtext-color">
            No reward distributions for this project yet.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-6">
      {/* Active period info */}
      <ActivePeriodCard
        projectSlug={projectSlug}
        isClaimRequired={isClaimRequired}
        hasClaimed={hasClaimed}
      />

      {/* Holder summary (if authenticated) */}
      {isAuthenticated && myData?.myRewards && myData.myRewards.length > 0 && (
        <HolderRewardsSummary
          myRewards={myData.myRewards}
          rewardCurrencyLabel={config.reward_currency_label}
        />
      )}

      {/* Distribution history */}
      <DistributionHistory
        projectSlug={projectSlug}
        myPastRewards={myData?.pastRewards}
        rewardCurrencyLabel={config.reward_currency_label}
      />
    </div>
  );
};

// ---- Legacy Claim System ----

const LegacyRewardsTab: React.FC<{ projectSlug: string }> = ({ projectSlug }) => {
  const { data: periodsData, isLoading } = usePublicPeriods(projectSlug);
  const periods = periodsData?.periods || [];

  return (
    <div className="flex w-full flex-col items-start gap-6">
      {/* Active period claim banner */}
      <ClaimBanner projectSlug={projectSlug} />

      {/* Period History */}
      <div className="flex w-full flex-col gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Reward Periods
        </span>

        {isLoading ? (
          <div className="text-subtext-color py-4">Loading reward periods...</div>
        ) : periods.length === 0 ? (
          <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
            <div className="flex flex-col items-center gap-2">
              <FeatherGift className="w-8 h-8 text-neutral-400" />
              <span className="text-body font-body text-subtext-color">
                No reward periods for this project yet.
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {periods.map(period => (
              <PeriodRow key={period.id} period={period} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const PeriodRow: React.FC<{ period: ClaimPeriod }> = ({ period }) => {
  const claimCount = period.live_claim_count ?? period.total_claims ?? 0;

  return (
    <div className="flex items-center justify-between rounded border border-solid border-neutral-border p-3">
      <div className="flex items-center gap-3">
        <Badge variant={
          period.status === 'open' ? 'success' :
          period.status === 'closed' ? 'warning' : 'neutral'
        }>
          {period.status}
        </Badge>
        <span className="text-body font-body text-default-font">
          {new Date(period.opened_at).toLocaleDateString()}
          {period.closed_at ? ` - ${new Date(period.closed_at).toLocaleDateString()}` : ' - present'}
        </span>
      </div>
      <span className="text-caption font-caption text-subtext-color">
        {Number(claimCount).toLocaleString()}
        {period.total_eligible_wallets ? ` / ${Number(period.total_eligible_wallets).toLocaleString()}` : ''} wallets
        {period.total_points_claimants != null && (
          <>, {Number(period.total_points_claimants).toLocaleString()}
          {period.total_points_all_holders ? ` / ${Number(period.total_points_all_holders).toLocaleString()}` : ''} pts</>
        )}
      </span>
    </div>
  );
};
